import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import classNames from 'classnames';
import compose from 'lodash.flowright';

import { mapProps } from '../../../../../../../../../../util/hocs';
import withModal from '../../../../../../../../../../components/global/modal/withModal';
import ShoppingBasketIcon from '../../../../../../../../../../components/icons/ShoppingBasketIcon';
import CheckboxIcon from '../../../../../../../../../../components/icons/CheckboxIcon';
import Button from '../../../../../../../../../../components/global/Button';
import InfoCircleTip from '../../../../../../../../../../components/global/InfoCircleTip';

export const PRINT_PACKAGE_CONFIGURABLE_ITEM_COMPLETE_MODAL =
  'modal/PRINT_PACKAGE_CONFIGURABLE_ITEM_COMPLETE';

const propTypes = {
  printPackage: PropTypes.shape({
    id: PropTypes.string,
  }),
  remainingConfigurableItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  completeConfigurableItem: PropTypes.shape({
    id: PropTypes.string,
  }),
  completeConfigurableItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  onClose: PropTypes.func,
  onCheckout: PropTypes.func,
  onConfigureNextItem: PropTypes.func,
};

const PrintPackageConfigurableItemCompleteModal = ({
  printPackage,
  remainingConfigurableItems,
  completeConfigurableItem,
  completeConfigurableItems,
  className,
  onClose,
  onCheckout,
  onConfigureNextItem,
}) => {
  const classes = classNames({
    [className]: !!className,
    'print-product-size-success': true,
  });

  const hasFutureDigitalConfigurations = !!printPackage?.items?.records?.find(
    item => !!item.digital_bundle || !!item.grants_purchased_identity,
  );
  const hasMoreConfigurableItems = remainingConfigurableItems?.length > 0;
  const productTitle = (() => {
    // Means we configured a group basically.
    if (completeConfigurableItems) {
      return `${pluralize('item', completeConfigurableItems.length, true)}`;
    }

    return (
      completeConfigurableItem?.print_product_configuration?.print_product
        ?.name || completeConfigurableItem?.custom_print?.name
    );
  })();

  return (
    <div className={classes}>
      <div className="print-product-size-success-header">
        <span className="print-product-size-success-header-icon">
          <ShoppingBasketIcon />
          <CheckboxIcon />
        </span>
        <h4>{productTitle}</h4>
        <p>
          {productTitle ? 'for ' : ''}
          <span className="text-dark">{printPackage.name}</span> has been
          configured!
          {hasFutureDigitalConfigurations && (
            <InfoCircleTip
              id="print-package-future-downloadables-tip"
              tip={
                <span className="text-center">
                  Digital photos included in this
                  <br />
                  package will be chosen after checkout
                </span>
              }
              className="ml-1 v-align-middle"
              infoCircleProps={{ size: 14 }}
            />
          )}
        </p>
      </div>

      {hasMoreConfigurableItems > 0 && (
        <p className="text-italic text-sm">
          <span className="text-dark text-bold">
            {remainingConfigurableItems?.length}
          </span>{' '}
          more {pluralize('item', remainingConfigurableItems.length)} to
          configure
        </p>
      )}

      <div className="print-product-size-success-actions">
        {hasMoreConfigurableItems ? (
          <Button
            btnBlock
            btnSize="lg"
            onClick={() => {
              onConfigureNextItem();
              onClose();
            }}
            btnStyle="primary"
            className="btn-checkout-print-product-size-success"
          >
            Configure my next item
          </Button>
        ) : (
          <Button
            btnBlock
            btnSize="lg"
            onClick={() => {
              onCheckout();
              onClose();
            }}
            btnStyle="success-vibrant"
            className="btn-checkout-print-product-size-success"
          >
            <ShoppingBasketIcon />
            Checkout
          </Button>
        )}
        <Button
          btnBlock
          btnSize="lg"
          btnOutline
          onClick={onClose}
          className="btn-checkout-print-product-size-success"
        >
          Continue shopping
        </Button>
      </div>
    </div>
  );
};

PrintPackageConfigurableItemCompleteModal.propTypes = propTypes;

export default withModal(
  compose(
    mapProps(({ onRequestClose, actionProps = {}, ...props }) => ({
      onClose: onRequestClose,
      ...actionProps,
      ...props,
    })),
  )(PrintPackageConfigurableItemCompleteModal),
  {
    name: PRINT_PACKAGE_CONFIGURABLE_ITEM_COMPLETE_MODAL,
    size: 'xs',
    hideHeader: true,
    dismissible: false,
    shouldCloseOnOverlayClick: false,
    className: 'print-product-size-success-modal-wrap',
  },
);
